import {Badge} from "@/components/Badge";
import {IBadgeType} from "@/components/Badge/Badge";
import {Button} from "@/components/Button";
import {ButtonProps} from "@/components/Button/Button";
import {Card} from "@/components/Card";
import {Dropdown} from "@/components/Dropdown";
import {Modal} from "@/components/Modal";
import {H3, P} from "@/components/Typography";
import {useOnboardingStatus} from "@/hooks/useOnboardingStatus";
import {useOnboardingProgression, useUninstallMatIntegration,} from "@/requests/hooks/mat";
import {faTrashAltSlash} from "@fortawesome/pro-duotone-svg-icons";
import {useQueryClient} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {tv} from "tailwind-variants";
import {Tooltip} from "@/components/Tooltip";
import {stringToTitleCaseWithUnderscore} from "@/utils/stringToTitleCase.ts";

const styles = tv({
  slots: {
    container: "flex flex-col gap-15 px-20 py-25",
    logoContainer:
      "flex h-50 w-50 items-center justify-center rounded-md bg-primary-light",
    logoImage: "h-auto w-25",
    channelsContainer: "flex flex-row gap-10",
  },
});

interface IButtonContext {
  isInternalLink: boolean;
  url: string;
  text: string;
  variant: ButtonProps["variant"];
}

interface IProps {
  matIntegration: IIntegration;
  refetchMatIntegrations: () => void;
}

const MatIntegrationCard = ({
  matIntegration,
  refetchMatIntegrations,
}: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { container, logoContainer, logoImage, channelsContainer } = styles();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>("#");

  const {
    logo,
    name,
    channels,
    authType,
    oauthUrl,
    integration,
    installationStatus,
    tooltip,
  } = matIntegration;
  const formattedName = stringToTitleCaseWithUnderscore(name);

  const {
    onboardingStatus: { totalSteps },
  } = useOnboardingStatus();

  const {
    mutate: uninstall,
    isSuccess: isUninstallSuccess,
    isPending: isUninstallPending,
    isError: isUninstallError,
  } = useUninstallMatIntegration(queryClient);

  const {
    mutate: completeOnboardingStep,
    isPending: isCompleteOnboardingStepPending,
    isSuccess: isCompleteOnboardingStepSuccess,
  } = useOnboardingProgression(queryClient);

  useEffect(() => {
    if (isUninstallSuccess) {
      toast.success(
        t("components.matIntegrationCard.success", { name: formattedName }),
      );
      toggleModalIsOpen();
      refetchMatIntegrations();
    }

    if (isUninstallError) {
      toast.error(
        t("components.matIntegrationCard.error", { name: formattedName }),
      );
      toggleModalIsOpen();
    }
  }, [isUninstallSuccess, isUninstallError]);

  useEffect(() => {
    if (isCompleteOnboardingStepSuccess) {
      window.location.replace(redirectUrl);
    }
  }, [isCompleteOnboardingStepSuccess]);

  const toggleModalIsOpen = () => setIsModalOpen(isModalOpen => !isModalOpen);

  const uninstallIntegration = () => uninstall({ integration });

  const renderChannels = () => {
    const getBadgeVariant = (
      status: IIntegrationChannelStatus,
    ): IBadgeType["value"] => {
      const statusMapping: Record<
        IIntegrationChannelStatus,
        IBadgeType["value"]
      > = {
        ENABLED: "success",
        DISABLED: "default",
        AVAILABLE: "info",
        NOT_AVAILABLE: "default",
        PROVISIONING: "warning",
        READY: "warning",
      };

      return statusMapping[status] ?? "default";
    };

    const getToolTipMessage = (status: IIntegrationChannelStatus): string => {
      const statusMapping: Record<IIntegrationChannelStatus, string> = {
        ENABLED: "Enabled",
        DISABLED: "Disabled",
        AVAILABLE: "Available",
        PROVISIONING: "Provisioning in progress",
        NOT_AVAILABLE: "Not available",
        READY: "Ready to enable",
      };

      return statusMapping[status] ?? status;
    };

    return channels.map(({ channel, status }, index) => (
      <Tooltip direction="bottom" content={getToolTipMessage(status)}>
        <Badge key={index} variant={getBadgeVariant(status)}>
          {channel}
        </Badge>
      </Tooltip>
    ));
  };

  const getButtonContext = (): IButtonContext => {
    if (authType === "OAUTH" && installationStatus === "AVAILABLE")
      return {
        url: oauthUrl ?? "#",
        text: t("components.matIntegrationCard.install"),
        variant: "primary",
        isInternalLink: false,
      };

    const integrationUrl = integration.toLocaleLowerCase();

    switch (installationStatus) {
      case "AVAILABLE":
        return {
          url: `/integrations/mat-settings/${integrationUrl}/authentication`,
          text: t("components.matIntegrationCard.install"),
          variant: "primary",
          isInternalLink: true,
        };
      case "DISABLED":
        return {
          url: `/integrations/mat-settings/${integrationUrl}/authentication`,
          text: t("components.matIntegrationCard.configure"),
          variant: "primary",
          isInternalLink: true,
        };
      case "INSTALLED":
      case "PROVISIONING":
      case "ENABLED":
        return {
          url: `/integrations/mat-settings/${integrationUrl}/configuration`,
          text: t("components.matIntegrationCard.configure"),
          variant: "success",
          isInternalLink: true,
        };
      case "NOT_AVAILABLE":
        return {
          url: "",
          text: t("components.matIntegrationCard.install"),
          variant: "primary",
          isInternalLink: true,
        };
    }
  };

  const renderButton = () => {
    const { text, variant, url, isInternalLink } = getButtonContext();

    if (isInternalLink) {
      return (
        <Button as="link" to={url} variant={variant} width="full">
          {text}
        </Button>
      );
    }

    const onClickHandler = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      /**
       * Set the redircet URL to trigger when the request is complete.
       */
      setRedirectUrl(url);

      /**
       * We want to mark the checklist step as "complete" for installing
       * an OAUTH integration at this point, as the user will never see
       * the Authentication page.
       *
       * However, previously onboarded customers may not have any steps
       * to mark as complete, resulting in an error. We should only mark
       * a step as complete if they have steps, otherwise we should just
       * redirect them straight away.
       */
      if (totalSteps > 0) {
        completeOnboardingStep("INSTALL_MAT_APP");
      } else {
        window.location.replace(url);
      }
    };

    return (
      <Button
        as="button"
        variant={variant}
        width="full"
        onClick={onClickHandler}
        loading={isCompleteOnboardingStepPending}>
        {text}
      </Button>
    );
  };

  const renderUninstallButton = () => {
    if (installationStatus === "AVAILABLE") return null;

    return (
      <Dropdown
        testId="uninstall-dropdown"
        variant="outline"
        button={{
          icon: faTrashAltSlash,
        }}
        items={[
          {
            leadingIcon: faTrashAltSlash,
            text: t("components.matIntegrationCard.uninstall"),
            onClick: toggleModalIsOpen,
            hoverColor: "red",
          },
        ]}
      />
    );
  };

  const renderUninstallModal = () => (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={toggleModalIsOpen}
      hideCloseButton>
      <div className="flex flex-col items-center gap-30">
        <H3>
          {t("components.matIntegrationCard.uninstallHeading", {
            name: formattedName,
          })}
        </H3>
        <P>
          {t("components.matIntegrationCard.confirmation", {
            name: formattedName,
          })}
        </P>
        <div className="flex flex-col gap-30 sm:flex-row">
          <Button
            variant="secondary-outline"
            width="md"
            onClick={toggleModalIsOpen}>
            {t("components.matIntegrationCard.cancel")}
          </Button>
          <Button
            variant="primary"
            width="md"
            onClick={uninstallIntegration}
            loading={isUninstallPending}>
            {t("components.matIntegrationCard.confirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );

  const renderCardBody = () => (
    <div className={container()} data-testid="mat-integration-card">
      <div className={logoContainer()}>
        <img className={logoImage()} src={logo} alt={formattedName} />
      </div>
      <H3 className="font-semibold">{formattedName}</H3>
      <div
        data-testid="mat-integration-card-channels"
        className={channelsContainer()}>
        {renderChannels()}
      </div>
      <Trans
        i18nKey="components.matIntegrationCard.blurb"
        values={{
          name: formattedName,
        }}>
        <p className="text-gray-dark">one</p>
      </Trans>
      <div className="flex flex-row gap-20">
        <div className="flex-grow">{renderButton()}</div>
        {renderUninstallButton()}
      </div>
    </div>
  );

  const renderCard = () => {
    if (installationStatus === "NOT_AVAILABLE") {
      return (
        <Tooltip content={tooltip}>
          <Card
            backgroundColor="primary"
            border={true}
            shadow={false}
            additionalClassName="pointer-events-none !cursor-not-allowed grayscale">
            {renderCardBody()}
          </Card>
        </Tooltip>
      );
    }

    return (
      <Card backgroundColor="primary" border={true} shadow={false}>
        {renderCardBody()}
      </Card>
    );
  };

  return (
    <>
      {renderCard()}
      {renderUninstallModal()}
    </>
  );
};

export default MatIntegrationCard;
